<template>
  <!-- container -->
  <div class="container mx-auto md:mx-0 p-8 md:p-12 md:w-2/3 h-full">
    <!-- Sign out  -->
    <div class="flex justify-end">
      <div class="flex flex-row text-gray-800 cursor-pointer" @click="signOut">
        <div class="md:visible invisible text-gray-600">
          {{ $t("buttons.signOut") }}
        </div>
        <font-awesome-icon color="grey" icon="sign-out-alt" class="ml-3 mt-1" />
      </div>
    </div>

    <!-- form section -->
    <div
      class="flex flex-col item-center md:items-start justify-center md:justify-start h-full md:pr-64 md:mt-0 mt-10"
    >
      <!-- logo -->
      <div
        class="flex item-center justify-center md:mb-12 mb-6 w-full flex-col md:flex-row"
      >
        <div class="md:w-1/3 md:w-56 w-full flex justify-center">
          <div class="flex flex-col md:mt-10 mt-0">
            <img src="@/assets/icons/logo.svg" />

            <div class="flex flex-row relative justify-center md:w-56">
              <!-- Info icon -->
              <rele-info-tooltip
                icon="info"
                @mouseover="onIconHover"
                @mouseleave="onIconLeave"
                :tooltip-html="$t('signup.info.tooltips.createOrganization')"
                class="absolute right-0 md:mr-4 mr-12"
              />

              <!-- SIGN UP text -->
              <div
                class="mt-5 text-r-primary text-2xl text-center tracking-widest"
              >
                {{ $t("signup.signup") }}
              </div>
            </div>
          </div>
        </div>

        <!-- steps -->
        <div class="flex w-2/3 w-full md:ml-10 ml-0 mt-8 md:mt-0">
          <rele-auth-steps :current-step="2" style="z-index: -10" />
        </div>
      </div>

      <!-- description -->
      <p v-html="$t('signup.createOrganizationDescription')" class="mb-6"></p>

      <!-- form -->
      <div
        v-if="isFormLoaderVisible"
        class="flex md:justify-start justify-center"
      >
        <r-loading size="large" />
      </div>
      <div v-else>
        <r-text-field
          id="workspace-input"
          v-model="workspaceName"
          class="w-48"
          :label="$t('forms.workspace')"
        />

        <!-- terms of service -->
        <div class="mt-5">
          <r-checkbox style="margin: 0.75em 0" v-model="tos">
            <a href="https://rele.ai/terms-of-service/" target="_blank">
              {{ $t("signup.tos") }}
            </a>
          </r-checkbox>
        </div>

        <!-- privacy policy -->
        <div class="mt-5">
          <r-checkbox class="mt-4" style="margin: 0.75em 0" v-model="privacy">
            <a href="https://rele.ai/privacy-policy/" target="_blank">
              {{ $t("signup.privacy") }}
            </a>
          </r-checkbox>
        </div>
      </div>

      <!-- submit button -->
      <r-button
        id="get-started-button"
        class="mt-16 md:mt-8"
        :disabled="isDisable"
        :loading="loading"
        @click="setupWorkspace"
      >
        {{ $t("buttons.getStarted") }}
      </r-button>
    </div>

    <!-- right side image -->
    <div
      :style="{ backgroundImage: `url(${bgUrl})` }"
      class="hidden md:block h-screen w-1/3 absolute top-0 right-0"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import CountryLanguage from "@ladjs/country-language"
import axios from "axios"
import firebase from "firebase/app"
import hubspot from "@/api/hubspot"
import bgUrl from "@/assets/images/background-img.png"
import componentAPI from "@/api/components"

const BOT_LANG = {
  en: 1,
  es: 2,
  fr: 3,
  gr: 4,
  inherit: 5,
  pt: 6
}

/**
 * Define the generate organization page
 */
export default {
  // component state
  data() {
    return {
      // background img url
      bgUrl,

      // workspace name
      workspace: "",

      loading: false,

      tos: false,
      privacy: false,
      claraNumber: "",
      botLang: "",
      botLangOptions: [
        {
          value: "EN",
          display: this.$t("botOptions.en")
        },
        {
          value: "ES",
          display: this.$t("botOptions.es")
        },
        {
          value: "FR",
          display: this.$t("botOptions.fr")
        },
        {
          value: "GR",
          display: this.$t("botOptions.gr")
        },
        {
          value: "PT",
          display: this.$t("botOptions.pt")
        }
      ]
    }
  },

  // calculated props
  computed: {
    ...mapGetters({
      hd: "auth/getHd",
      locale: "auth/getLocale",
      user: "auth/getUser",
      lastName: "auth/getLastName",
      firstName: "auth/getFirstName",
      mobile: "auth/getMobile",
      contactId: "hubspot/getReleCrmContactId",
      userFsId: "auth/getUserFsId"
    }),

    // handle the workspace name
    workspaceName: {
      get() {
        return this.workspace || this.hd
      },

      set(val) {
        this.workspace = val
      }
    },

    // is button disable
    isDisable() {
      return !this.tos || !this.privacy || !this.workspaceName.trim()
    },

    /**
     * Is form visible
     */
    isFormLoaderVisible() {
      return Boolean(this.botLang === "" || this.claraNumber === "")
    },

    // clara number list
    claraNumberList() {
      if (process.env.VUE_APP_FIRE_ENV === "production") {
        return [
          { display: "International", value: "41772022053" },
          { display: "Colombia", value: "573195037823" },
          { display: "Mexico", value: "525544622329" }
        ]
      } else {
        return [{ display: "International", value: "41772016402" }]
      }
    },

    /**
     * fallbackClaraNumber returns the Intenational number
     * from claraNumberList
     */
    fallbackClaraNumber() {
      return this.claraNumberList.find((country) => {
        return country.display === "International"
      })
    }
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  /**
   * Mounted hook
   */
  async mounted() {
    this.botLang = await this.getInitBotLang()
    this.claraNumber = await this.getInitClaraNumber()
  },

  // component functions
  methods: {
    /**
     * Create the organization for the workspace
     */
    async createOrg() {
      try {
        // create organization
        return await this.$store.dispatch("orgs/add", {
          name: this.workspaceName,
          tos: this.tos,
          privacy: this.privacy,
          claraNumber: this.claraNumber,
          botLang: BOT_LANG[this.botLang.toLowerCase()]
        })
      } catch (e) {
        console.error("unable to create org", e)
        this.notifyError({
          title: this.$t("errors.unableToCreateNewOrg"),
          message: e.message
        })
      }
    },

    /**
     * Update the user
     */
    async updateUser(orgId, accessLevel) {
      try {
        const userUpdate = {
          orgs: [orgId],
          auth: { accessLevel }
        }
        const { claims } = await firebase.auth().currentUser.getIdTokenResult()

        console.log("userUpdate ", userUpdate)
        console.log("claims", claims)

        const updatedres = await this.$store.dispatch("users/update", {
          id: claims.userFsId,
          data: userUpdate
        })
        console.log("updatedres", updatedres)
        location.replace("/auth/first-integration")
      } catch (e) {
        console.error("unable to update user", e)
        this.notifyError({
          title: this.$t("errors.unableToUpdateUserWithOrgId")
        })
      }
    },

    /**
     * Initialize the organization and update the user
     */
    async setupWorkspace() {
      if (this.workspaceName) {
        this.loading = true
        try {
          // create org on firestore
          const orgRes = await this.createOrg()

          // create user on RELE's CRM - STEP3
          if (process.env.VUE_APP_FIRE_ENV === "production") {
            await this.updateRecordReleCrm(this.workspaceName)
          }

          // update user with organization id
          return await this.updateUser(orgRes.id, 10)
        } catch (e) {
          console.error("unable to setup workspace", e)
          this.loading = false
        }
      } else {
        // notify user that the workspace name is required
        this.notifyError({
          title: this.$t("errors.workspaceNameRequired")
        })
      }
    },

    /**
     * Create user on RELE.AI crm if not exists
     */
    async updateRecordReleCrm(orgName) {
      // set api
      const jwt = await firebase.auth().currentUser.getIdToken()

      const api = hubspot("NONE_API", jwt)
      const contactId = await this.getContactId()

      if (contactId) {
        // create contact
        try {
          await api.notify("update_contact_rele_crm", {
            id: contactId.toString(),
            company: {
              properties: [{ name: "name", value: orgName }]
            },
            contact: {
              properties: [
                { property: "product_lifecycle_stage", value: "Lead" }
              ]
            }
          })
        } catch (e) {
          console.error(e)
          console.error(
            "Cannot set user to RELE.AI CRM. Maybe user already exsits on RELE's CRM"
          )
        }
      }
    },

    /**
     * Get contact ID
     */
    async getContactId() {
      return this.contactId ? this.contactId : await this.searchContact()
    },

    /**
     * Search contact on RELE CRM
     */
    async searchContact() {
      // set api
      const jwt = await firebase.auth().currentUser.getIdToken()
      const api = hubspot("NONE_API", jwt)

      try {
        const searchContactId = await api.searchContactReleCrm({
          contact: {
            properties: [{ property: "email", value: this.user.email }]
          }
        })

        return searchContactId
      } catch (e) {
        console.error("Cannot find any contact match on RELE.AI CRM")
      }
    },

    /**
     * Returns initial bot language
     */
    async getInitBotLang() {
      // init bot lang var
      let initBotLang = ""

      // resolve promise and get data from ip
      try {
        const loc = await this.getInfoFromIP()

        // find common language between existing Bot Langs
        // and user's IP info
        const countryCode = loc.data?.country_code || "US"
        const langs = await this.countryCodeToLanguage(countryCode)
        const supportedBotLangs = Object.keys(BOT_LANG)
        const commonLang = this.getCommonLang(supportedBotLangs, langs)

        // determine init bot lang
        if (commonLang) {
          initBotLang = commonLang.toUpperCase()
        } else if (BOT_LANG[this.locale]) {
          initBotLang = this.locale.toUpperCase()
        } else {
          initBotLang = "EN"
        }

        return initBotLang
      } catch (e) {
        return "EN"
      }
    },

    /**
     * Get into from ip
     */
    getInfoFromIP() {
      return axios.get("https://json.geoiplookup.io/")
    },

    /**
     * Format country code to language
     */
    countryCodeToLanguage(countryCode) {
      return new Promise((resolve, reject) => {
        CountryLanguage.getCountryLanguages(countryCode, (err, languages) => {
          if ((languages || []).length) {
            resolve(languages.map((lang) => lang.iso639_1))
          }

          reject([])
        })
      })
    },

    /**
     * Get common lang between supported lang and ip info
     */
    getCommonLang(supportedBotLangs, langs) {
      const commonLangs = supportedBotLangs.filter((supportLang) => {
        return langs.includes(supportLang)
      })

      if (commonLangs.length) {
        return commonLangs[0]
      }

      return ""
    },

    /**
     * getInitClaraNumber returns the clara number that should
     * be set using ip address.
     */
    async getInitClaraNumber() {
      let initClaraNumber = ""

      try {
        // pull country name
        const loc = await this.getInfoFromIP()
        const countryName = loc.data?.country_name || ""

        // find common country names between
        // claraNumberList and the current countryName
        const commonCountry = this.claraNumberList.find((country) => {
          return country.display === countryName
        })

        // Set Clara Number
        if (Object.keys(commonCountry || {}).length) {
          initClaraNumber = commonCountry.value
        } else {
          initClaraNumber = this.fallbackClaraNumber.value
        }

        // returns the init clara number
        return initClaraNumber
      } catch (e) {
        return this.fallbackClaraNumber.value
      }
    },

    /**
     * signOut sign out the user from rele.ai
     * delete his authentication and his user.
     */
    async signOut() {
      // define auth and current user, and jwt
      const fsAuth = firebase.auth()
      const user = fsAuth.currentUser
      const jwt = await user.getIdToken()

      try {
        // delete fb-auth
        await componentAPI
          .users(jwt)
          .deleteUserWithAuth(user.uid, this.userFsId)

        // reload page
        location.reload()
      } catch (e) {
        console.log("e", e)
        this.notifyError({
          title: this.$t("errors.unableToSignOut"),
          message: "Unable to sign out user"
        })
      }
    }
  }
}
</script>
